import React, { useState, useEffect, useRef } from 'react';
import { urls } from './config.dev';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner, faInfoCircle, faPlus, faTrash, faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// ─── MOBILE DETECTION HOOK ───────────────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

// ─── THEME DEFINITIONS ────────────────────────────────────────────────────────
const themes = {
  blue: {
    primary: '#2563eb', primaryHover: '#1d4ed8', primaryLight: '#3b82f6', primaryBg: '#eff6ff',
    success: '#10b981', successHover: '#059669', danger: '#ef4444', dangerHover: '#dc2626',
    warning: '#f59e0b', warningLight: '#fef3c7', textPrimary: '#111827', textSecondary: '#4b5563',
    textMuted: '#6b7280', border: '#e5e7eb', background: '#ffffff', cardBg: '#f9fafb',
    infoBg: '#f0f9ff', infoBorder: '#0ea5e9', infoText: '#0c4a6e', overlay: 'rgba(0,0,0,0.6)',
    closeIcon: '#6b7280', closeIconHover: '#ef4444', tabInactive: '#6b7280', tabActive: '#2563eb',
    tabActiveBorder: '#3b82f6', tabHoverBg: '#f3f4f6', tabHoverText: '#4b5563',
    inputBorder: '#d1d5db', inputFocus: '#2563eb', disabled: '#9ca3af',
    unitCardBg: '#f9fafb', unitCardBorder: '#e5e7eb', addButtonBg: '#10b981', addButtonHover: '#059669',
  },
  white: {
    primary: '#1e293b', primaryHover: '#0f172a', primaryLight: '#334155', primaryBg: '#f8fafc',
    success: '#16a34a', successHover: '#15803d', danger: '#dc2626', dangerHover: '#b91c1c',
    warning: '#d97706', warningLight: '#fffbeb', textPrimary: '#0f172a', textSecondary: '#334155',
    textMuted: '#475569', border: '#e2e8f0', background: '#ffffff', cardBg: '#f1f5f9',
    infoBg: '#f8fafc', infoBorder: '#cbd5e1', infoText: '#1e293b', overlay: 'rgba(0,0,0,0.5)',
    closeIcon: '#64748b', closeIconHover: '#dc2626', tabInactive: '#475569', tabActive: '#0f172a',
    tabActiveBorder: '#0f172a', tabHoverBg: '#f1f5f9', tabHoverText: '#0f172a',
    inputBorder: '#cbd5e1', inputFocus: '#0f172a', disabled: '#94a3b8',
    unitCardBg: '#ffffff', unitCardBorder: '#e2e8f0', addButtonBg: '#16a34a', addButtonHover: '#15803d',
  }
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const createEmptyStockItem = () => ({ quantity: 0, batchNumber: '', expiryDate: '' });

const formatDateForInput = (d) => {
  if (!d || d === '001/001/0001' || d === '01/01/0001') return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  const parts = d.split('/');
  if (parts.length === 3) return parts[0].length === 4 ? `${parts[0]}-${parts[1]}-${parts[2]}` : `${parts[2]}-${parts[1]}-${parts[0]}`;
  return '';
};

const formatDateForPayload = (d) => {
  if (!d) return '001/001/0001';
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const [y, m, day] = d.split('-');
    return `${day}/${m}/${y}`;
  }
  return d;
};

// ─── SHARED INPUT STYLES ──────────────────────────────────────────────────────
const getInputStyle = (theme, isMobile) => ({
  width: '100%', padding: isMobile ? '12px 14px' : '10px 12px',
  border: `1px solid ${theme.inputBorder}`, borderRadius: '8px',
  color: theme.textPrimary, fontSize: isMobile ? '15px' : '14px',
  outline: 'none', background: theme.background, boxSizing: 'border-box',
  WebkitAppearance: 'none',
});

const getLabelStyle = (theme, isMobile) => ({
  display: 'block', marginBottom: '6px', color: theme.textSecondary,
  fontWeight: '600', fontSize: isMobile ? '13px' : '12px', letterSpacing: '0.02em',
});

// ─── MOBILE TAB BAR ───────────────────────────────────────────────────────────
function MobileTabBar({ activeTab, onTabChange, theme }) {
  const tabs = [
    { id: 'details', icon: '📋', label: 'Details' },
    { id: 'stock', icon: '📦', label: 'Stock' },
  ];
  return (
    <div style={{ display: 'flex', borderBottom: `1px solid ${theme.border}`, background: theme.background, position: 'sticky', top: 0, zIndex: 10 }}>
      {tabs.map(tab => (
        <button key={tab.id} onClick={() => onTabChange(tab.id)} style={{
          flex: 1, padding: '14px 8px', border: 'none', background: 'transparent', cursor: 'pointer',
          borderBottom: activeTab === tab.id ? `3px solid ${theme.tabActiveBorder}` : '3px solid transparent',
          color: activeTab === tab.id ? theme.tabActive : theme.tabInactive,
          fontSize: '13px', fontWeight: activeTab === tab.id ? '600' : '500',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px',
          transition: 'all 0.15s ease',
        }}>
          <span style={{ fontSize: '18px' }}>{tab.icon}</span>
          {tab.label}
        </button>
      ))}
    </div>
  );
}

// ─── STOCK ITEM ROW ───────────────────────────────────────────────────────────
function StockItemRow({ item, index, theme, isMobile, useBatch, useExpiry, onUpdate, onRemove }) {
  const inputStyle = getInputStyle(theme, isMobile);
  const labelStyle = getLabelStyle(theme, isMobile);

  if (isMobile) {
    return (
      <div style={{ background: theme.unitCardBg, border: `1px solid ${theme.unitCardBorder}`, borderRadius: '10px', padding: '14px', marginBottom: '10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <span style={{ fontSize: '12px', fontWeight: '600', color: theme.textMuted }}>Entry #{index + 1}</span>
          <button onClick={onRemove} style={{ width: '30px', height: '30px', borderRadius: '8px', border: `1px solid ${theme.danger}`, background: 'rgba(239,68,68,0.08)', color: theme.danger, cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div>
            <label style={labelStyle}>Quantity *</label>
            <input type="number" style={inputStyle} placeholder="0" value={item.quantity} onChange={e => onUpdate('quantity', e.target.value)} onFocus={e => e.target.style.borderColor = theme.inputFocus} onBlur={e => e.target.style.borderColor = theme.inputBorder} />
          </div>
          {useBatch && (
            <div>
              <label style={labelStyle}>Batch Number</label>
              <input type="text" style={inputStyle} placeholder="e.g., BN-2024-001" value={item.batchNumber === '0000' ? '' : item.batchNumber} onChange={e => onUpdate('batchNumber', e.target.value)} onFocus={e => e.target.style.borderColor = theme.inputFocus} onBlur={e => e.target.style.borderColor = theme.inputBorder} />
            </div>
          )}
          {useExpiry && (
            <div>
              <label style={labelStyle}>Expiry Date</label>
              <input type="date" style={inputStyle} value={formatDateForInput(item.expiryDate)} onChange={e => onUpdate('expiryDate', e.target.value)} onFocus={e => e.target.style.borderColor = theme.inputFocus} onBlur={e => e.target.style.borderColor = theme.inputBorder} />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Desktop
  const cols = useBatch && useExpiry ? '1fr 1fr 1fr' : (useBatch || useExpiry ? '1fr 1fr' : '1fr');
  return (
    <div style={{ background: theme.unitCardBg, border: `1px solid ${theme.unitCardBorder}`, borderRadius: '8px', padding: '15px', marginBottom: '12px', position: 'relative' }}>
      <button onClick={onRemove} style={{ position: 'absolute', top: '10px', right: '10px', background: 'none', border: 'none', color: theme.danger, cursor: 'pointer', fontSize: '14px' }} title="Remove">
        <FontAwesomeIcon icon={faTrash} />
      </button>
      <div style={{ display: 'grid', gridTemplateColumns: cols, gap: '14px', paddingRight: '30px' }}>
        <div>
          <label style={getLabelStyle(theme, false)}>Quantity *</label>
          <input type="number" style={getInputStyle(theme, false)} placeholder="Quantity" value={item.quantity} onChange={e => onUpdate('quantity', e.target.value)} onFocus={e => e.target.style.borderColor = theme.inputFocus} onBlur={e => e.target.style.borderColor = theme.inputBorder} />
        </div>
        {useBatch && (
          <div>
            <label style={getLabelStyle(theme, false)}>Batch Number</label>
            <input type="text" style={getInputStyle(theme, false)} placeholder="Batch Number" value={item.batchNumber === '0000' ? '' : item.batchNumber} onChange={e => onUpdate('batchNumber', e.target.value)} onFocus={e => e.target.style.borderColor = theme.inputFocus} onBlur={e => e.target.style.borderColor = theme.inputBorder} />
          </div>
        )}
        {useExpiry && (
          <div>
            <label style={getLabelStyle(theme, false)}>Expiry Date</label>
            <input type="date" style={getInputStyle(theme, false)} value={formatDateForInput(item.expiryDate)} onChange={e => onUpdate('expiryDate', e.target.value)} onFocus={e => e.target.style.borderColor = theme.inputFocus} onBlur={e => e.target.style.borderColor = theme.inputBorder} />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MAIN MODAL COMPONENT ─────────────────────────────────────────────────────
function UpdateStocksModal({ isOpen, onClose, selectedDrug, drugDetails, token, refreshDrugs, themeColor = 'blue' }) {
  const isMobile = useIsMobile();
  const normalizedTheme = (themeColor?.toLowerCase() === 'white' || themeColor?.toLowerCase() === 'null') ? 'white' : 'blue';
  const theme = themes[normalizedTheme];

  const useDrugBatchNumbers = selectedDrug?.use_drug_batch_numbers === 'yes';
  const useDrugExpiryDate = selectedDrug?.use_drug_expiry_date === 'yes';
  const isBatchOrExpiryActive = useDrugBatchNumbers || useDrugExpiryDate;

  const hasLoadedInitialData = useRef(false);
  const drugIdRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      const drugData = selectedDrug || drugDetails;
      if (drugData?.drug_id && drugData.drug_id !== drugIdRef.current) {
        hasLoadedInitialData.current = false;
        drugIdRef.current = drugData.drug_id;
      }
    }
  }, [isOpen, selectedDrug, drugDetails]);

  const [activeTab, setActiveTab] = useState('details');
  const [originalDrugDetails, setOriginalDrugDetails] = useState(null);
  const [drugDetailsState, setDrugDetailsState] = useState({ drugId: '', drugName: '', packaging: '', warningPoint: '', costPrice: '', sellingPrice: '', additionalInfo: '' });
  const [dispensaryStockItems, setDispensaryStockItems] = useState([createEmptyStockItem()]);
  const [storeStockItems, setStoreStockItems] = useState([createEmptyStockItem()]);
  const [originalDispensaryStockItems, setOriginalDispensaryStockItems] = useState([]);
  const [originalStoreStockItems, setOriginalStoreStockItems] = useState([]);
  const [simpleDispensaryStock, setSimpleDispensaryStock] = useState(0);
  const [simpleStoreStock, setSimpleStoreStock] = useState(0);
  const [originalSimpleDispensaryStock, setOriginalSimpleDispensaryStock] = useState(0);
  const [originalSimpleStoreStock, setOriginalSimpleStoreStock] = useState(0);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isUpdatingStock, setIsUpdatingStock] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const availablePackaging = ['Boxes', 'Packets', 'Sackets', 'Pieces', 'Rolls', 'Bottles', 'kgs', 'ltrs'];

  const getTotalDispensaryStock = () => isBatchOrExpiryActive ? dispensaryStockItems.reduce((s, i) => s + (parseInt(i.quantity) || 0), 0) : simpleDispensaryStock;
  const getTotalStoreStock = () => isBatchOrExpiryActive ? storeStockItems.reduce((s, i) => s + (parseInt(i.quantity) || 0), 0) : simpleStoreStock;

  useEffect(() => {
    const drugData = selectedDrug || drugDetails;
    if (isOpen && drugData && !hasLoadedInitialData.current) {
      const nd = {
        drugId: drugData.drug_id || drugData.drugId || '',
        drugName: drugData.drug_name || drugData.drugName || '',
        packaging: drugData.packaging || '',
        warningPoint: drugData.warning_point || drugData.warningPoint || '',
        costPrice: drugData.cost_price || drugData.costPrice || '',
        sellingPrice: drugData.selling_price || drugData.sellingPrice || '0',
        additionalInfo: drugData.additional_info || drugData.additionalInfo || '',
      };
      setDrugDetailsState(nd);
      setOriginalDrugDetails(nd);
      fetchDrugQuantities(drugData);
      hasLoadedInitialData.current = true;
    }
    if (!isOpen) {
      hasLoadedInitialData.current = false;
      drugIdRef.current = null;
      setOriginalDrugDetails(null);
      setDispensaryStockItems([createEmptyStockItem()]);
      setStoreStockItems([createEmptyStockItem()]);
      setSimpleDispensaryStock(0);
      setSimpleStoreStock(0);
      setActiveTab('details');
    }
  }, [isOpen, selectedDrug, drugDetails]);

  const fetchDrugQuantities = (drugData) => {
    setIsLoading(true);
    const payload = { drugId: drugData.drug_id, drugName: drugData.drug_name || drugData.drugName, packaging: drugData.packaging, token, useBatchNumbers: useDrugBatchNumbers, useExpiryDate: useDrugExpiryDate, batchNumbersEnabled: useDrugBatchNumbers, expiryDateEnabled: useDrugExpiryDate };
    fetch(urls.fetchdrugquantity, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
      .then(r => r.json())
      .then(data => {
        const hasBatch = data.dispensaryStock && Array.isArray(data.dispensaryStock);
        if (hasBatch) {
          const disp = data.dispensaryStock.length > 0 ? data.dispensaryStock.map(i => ({ quantity: i.quantity || 0, batchNumber: i.batch_number || '', expiryDate: i.expiry_date || '' })) : [createEmptyStockItem()];
          setDispensaryStockItems(disp);
          setOriginalDispensaryStockItems(JSON.parse(JSON.stringify(disp)));
          const stor = data.storeStock && Array.isArray(data.storeStock) && data.storeStock.length > 0 ? data.storeStock.map(i => ({ quantity: i.quantity || 0, batchNumber: i.batch_number || '', expiryDate: i.expiry_date || '' })) : [createEmptyStockItem()];
          setStoreStockItems(stor);
          setOriginalStoreStockItems(JSON.parse(JSON.stringify(stor)));
        } else {
          setSimpleDispensaryStock(data.dispensaryStock || 0);
          setSimpleStoreStock(data.storeStock || 0);
          setOriginalSimpleDispensaryStock(data.dispensaryStock || 0);
          setOriginalSimpleStoreStock(data.storeStock || 0);
        }
      })
      .catch(e => toast.error('Error fetching quantities: ' + e.message))
      .finally(() => setIsLoading(false));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDrugDetailsState(p => ({ ...p, [name]: value }));
  };

  const handleUpdateDrug = () => {
    setIsUpdating(true);
    if (!drugDetailsState.drugName || !drugDetailsState.packaging || !drugDetailsState.warningPoint || !drugDetailsState.costPrice || !drugDetailsState.sellingPrice) {
      toast.error('Please fill in all fields.'); setIsUpdating(false); return;
    }
    const drugData = selectedDrug || drugDetails;
    const payload = {
      drugId: drugDetailsState.drugId,
      oldDetails: { drugName: drugData.drug_name || drugData.drugName, packaging: drugData.packaging, warningPoint: drugData.warning_point || drugData.warningPoint, costPrice: drugData.cost_price || drugData.costPrice, sellingPrice: drugData.selling_price || drugData.sellingPrice || '0' },
      newDetails: { ...drugDetailsState, sellingPrice: drugDetailsState.sellingPrice || '0' },
      token, useBatchNumbers: useDrugBatchNumbers, useExpiryDate: useDrugExpiryDate,
    };
    fetch(urls.updatedrugdetails, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
      .then(r => r.json())
      .then(data => {
        if (data.success) { toast.success('Item details updated!'); setOriginalDrugDetails({ ...drugDetailsState }); refreshDrugs(); }
        else toast.error(`Error: ${data.message || 'Failed to update'}`);
      })
      .catch(() => toast.error('Unexpected error. Please try again.'))
      .finally(() => setIsUpdating(false));
  };

  const handleUpdateStock = () => {
    setIsUpdatingStock(true);
    let payload;
    if (isBatchOrExpiryActive) {
      const filtDisp = dispensaryStockItems.filter(i => i.quantity && parseInt(i.quantity) > 0);
      const filtStor = storeStockItems.filter(i => i.quantity && parseInt(i.quantity) > 0);
      payload = {
        drugId: drugDetailsState.drugId, drugName: drugDetailsState.drugName, packaging: drugDetailsState.packaging, token,
        costPrice: drugDetailsState.costPrice, sellingPrice: drugDetailsState.sellingPrice || '0',
        useBatchNumbers: useDrugBatchNumbers, useExpiryDate: useDrugExpiryDate, batchNumbersEnabled: useDrugBatchNumbers, expiryDateEnabled: useDrugExpiryDate,
        dispensaryStockItems: (filtDisp.length > 0 ? filtDisp : [createEmptyStockItem()]).map(i => ({ quantity: parseInt(i.quantity) || 0, batchNumber: i.batchNumber || '0000', expiryDate: formatDateForPayload(i.expiryDate) || '001/001/0001' })),
        storeStockItems: (filtStor.length > 0 ? filtStor : [createEmptyStockItem()]).map(i => ({ quantity: parseInt(i.quantity) || 0, batchNumber: i.batchNumber || '0000', expiryDate: formatDateForPayload(i.expiryDate) || '001/001/0001' }))
      };
    } else {
      payload = { drugId: drugDetailsState.drugId, drugName: drugDetailsState.drugName, packaging: drugDetailsState.packaging, token, dispensaryStock: simpleDispensaryStock, storeStock: simpleStoreStock, costPrice: drugDetailsState.costPrice, sellingPrice: drugDetailsState.sellingPrice || '0', useBatchNumbers: useDrugBatchNumbers, useExpiryDate: useDrugExpiryDate, batchNumbersEnabled: useDrugBatchNumbers, expiryDateEnabled: useDrugExpiryDate };
    }
    const endpoint = isBatchOrExpiryActive ? urls.updatestockfiguresbatch : urls.updatestockfigures;
    fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          toast.success('Stock updated successfully!');
          if (isBatchOrExpiryActive) { setOriginalDispensaryStockItems(JSON.parse(JSON.stringify(dispensaryStockItems))); setOriginalStoreStockItems(JSON.parse(JSON.stringify(storeStockItems))); }
          else { setOriginalSimpleDispensaryStock(simpleDispensaryStock); setOriginalSimpleStoreStock(simpleStoreStock); }
          refreshDrugs();
        } else toast.error(`Error: ${data.message || 'Failed to update stock'}`);
      })
      .catch(() => toast.error('Unexpected error. Please try again.'))
      .finally(() => setIsUpdatingStock(false));
  };

  const isDetailsChanged = () => {
    if (!originalDrugDetails) return false;
    return ['drugName', 'packaging', 'warningPoint', 'costPrice', 'sellingPrice', 'additionalInfo'].some(k => drugDetailsState[k] !== originalDrugDetails[k]);
  };

  const isStockChanged = () => {
    if (isBatchOrExpiryActive) {
      if (dispensaryStockItems.length !== originalDispensaryStockItems.length || storeStockItems.length !== originalStoreStockItems.length) return true;
      return dispensaryStockItems.some((item, i) => {
        const o = originalDispensaryStockItems[i] || {};
        return parseInt(item.quantity) !== parseInt(o.quantity) || item.batchNumber !== o.batchNumber || item.expiryDate !== o.expiryDate;
      }) || storeStockItems.some((item, i) => {
        const o = originalStoreStockItems[i] || {};
        return parseInt(item.quantity) !== parseInt(o.quantity) || item.batchNumber !== o.batchNumber || item.expiryDate !== o.expiryDate;
      });
    }
    return simpleDispensaryStock !== originalSimpleDispensaryStock || simpleStoreStock !== originalSimpleStoreStock;
  };

  if (!isOpen) return null;

  const inputStyle = getInputStyle(theme, isMobile);
  const labelStyle = getLabelStyle(theme, isMobile);
  const formGroupStyle = { marginBottom: isMobile ? '14px' : '18px' };

  const SaveButton = ({ onClick, loading, disabled, label, loadingLabel }) => (
    <button onClick={onClick} disabled={disabled || loading} style={{
      width: '100%', padding: isMobile ? '14px' : '11px 20px', border: 'none', borderRadius: '8px',
      background: (disabled || loading) ? theme.disabled : theme.primary,
      color: '#fff', fontSize: isMobile ? '15px' : '14px', fontWeight: '600',
      cursor: (disabled || loading) ? 'not-allowed' : 'pointer', transition: 'all 0.2s ease',
    }}>
      {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : label}
    </button>
  );

  const AddRowButton = ({ onClick, label }) => (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
      width: '100%', padding: isMobile ? '12px' : '10px 15px',
      background: theme.addButtonBg, color: '#fff', border: 'none',
      borderRadius: '8px', cursor: 'pointer', fontSize: isMobile ? '14px' : '13px',
      fontWeight: '500', marginBottom: '14px', transition: 'all 0.2s ease',
    }}>
      <FontAwesomeIcon icon={faPlus} />
      {label}
    </button>
  );

  // ── MOBILE MODAL ────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: theme.background, zIndex: 1000, display: 'flex', flexDirection: 'column', overflowY: 'hidden' }}>

        {/* Mobile Header */}
        <div style={{ background: theme.background, borderBottom: `1px solid ${theme.border}`, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
          <button onClick={onClose} style={{ width: '36px', height: '36px', borderRadius: '8px', border: `1px solid ${theme.border}`, background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.textSecondary }}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '15px', fontWeight: '700', color: theme.textPrimary }}>Edit Item</div>
            <div style={{ fontSize: '12px', color: theme.textMuted }}>{drugDetailsState.drugName}</div>
          </div>
          {isBatchOrExpiryActive && (
            <div style={{ fontSize: '11px', background: theme.primaryBg, color: theme.primary, padding: '4px 8px', borderRadius: '20px', fontWeight: '600' }}>
              {useDrugBatchNumbers && useDrugExpiryDate ? '📦+📅' : useDrugBatchNumbers ? '📦' : '📅'}
            </div>
          )}
        </div>

        {/* Tab Bar */}
        <MobileTabBar activeTab={activeTab} onTabChange={setActiveTab} theme={theme} />

        {/* Scrollable Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px', WebkitOverflowScrolling: 'touch' }}>
          {isLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '200px', gap: '16px' }}>
              <FontAwesomeIcon icon={faSpinner} spin size="2x" style={{ color: theme.primary }} />
              <div style={{ color: theme.textMuted, fontSize: '13px' }}>Loading item data...</div>
            </div>
          ) : (
            <>
              {/* DETAILS TAB */}
              {activeTab === 'details' && (
                <div>
                  <div style={{ background: theme.infoBg, border: `1px solid ${theme.infoBorder}`, borderRadius: '10px', padding: '12px', marginBottom: '20px', fontSize: '13px', color: theme.infoText }}>
                    <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '8px' }} />
                    Update item details below. Changes apply across all modules.
                  </div>

                  {[
                    { label: 'Item Name', name: 'drugName', type: 'text', placeholder: 'Item name' },
                    { label: 'Warning Point (Min. Stock)', name: 'warningPoint', type: 'number', placeholder: 'e.g., 10' },
                    { label: 'Cost Price (UGX)', name: 'costPrice', type: 'number', placeholder: 'Purchase price' },
                    { label: 'Selling Price (UGX)', name: 'sellingPrice', type: 'number', placeholder: 'Selling price' },
                  ].map(field => (
                    <div key={field.name} style={formGroupStyle}>
                      <label style={labelStyle}>{field.label}</label>
                      <input type={field.type} name={field.name} style={inputStyle} placeholder={field.placeholder} value={drugDetailsState[field.name]} onChange={handleInputChange} onFocus={e => e.target.style.borderColor = theme.inputFocus} onBlur={e => e.target.style.borderColor = theme.inputBorder} />
                    </div>
                  ))}

                  <div style={formGroupStyle}>
                    <label style={labelStyle}>Packaging</label>
                    <select name="packaging" style={inputStyle} value={drugDetailsState.packaging} onChange={handleInputChange} onFocus={e => e.target.style.borderColor = theme.inputFocus} onBlur={e => e.target.style.borderColor = theme.inputBorder}>
                      <option value="">Select Packaging</option>
                      {availablePackaging.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>

                  <div style={{ marginTop: '24px' }}>
                    <SaveButton onClick={handleUpdateDrug} loading={isUpdating} disabled={!isDetailsChanged()} label="Save Item Details" loadingLabel="Saving..." />
                  </div>
                </div>
              )}

              {/* STOCK TAB */}
              {activeTab === 'stock' && (
                <div>
                  {/* Summary Chips */}
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                    {[
                      { label: '🏪 Shelves', value: getTotalDispensaryStock() },
                      { label: '📦 Store', value: getTotalStoreStock() },
                    ].map(s => (
                      <div key={s.label} style={{ flex: 1, background: theme.primaryBg, borderRadius: '10px', padding: '12px', textAlign: 'center', border: `1px solid ${theme.border}` }}>
                        <div style={{ fontSize: '11px', color: theme.textMuted, marginBottom: '4px', fontWeight: '600' }}>{s.label}</div>
                        <div style={{ fontSize: '22px', fontWeight: '700', color: theme.primary }}>{s.value}</div>
                        <div style={{ fontSize: '10px', color: theme.textMuted }}>units</div>
                      </div>
                    ))}
                  </div>

                  {/* Shelves Stock */}
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <h3 style={{ fontSize: '15px', fontWeight: '700', color: theme.textPrimary }}>🏪 Shelves Stock</h3>
                      <span style={{ fontSize: '12px', color: theme.textMuted, background: theme.cardBg, padding: '3px 8px', borderRadius: '20px', border: `1px solid ${theme.border}` }}>{getTotalDispensaryStock()} units</span>
                    </div>
                    {isBatchOrExpiryActive ? (
                      <>
                        {dispensaryStockItems.map((item, i) => (
                          <StockItemRow key={i} item={item} index={i} theme={theme} isMobile={true} useBatch={useDrugBatchNumbers} useExpiry={useDrugExpiryDate}
                            onUpdate={(field, val) => { const u = [...dispensaryStockItems]; u[i][field] = val; setDispensaryStockItems(u); }}
                            onRemove={() => { if (dispensaryStockItems.length <= 1) { toast.warning('At least one entry required'); return; } const u = [...dispensaryStockItems]; u.splice(i, 1); setDispensaryStockItems(u); }}
                          />
                        ))}
                        <AddRowButton onClick={() => setDispensaryStockItems([...dispensaryStockItems, createEmptyStockItem()])} label="Add Shelves Row" />
                      </>
                    ) : (
                      <div style={formGroupStyle}>
                        <label style={labelStyle}>Shelves Quantity</label>
                        <input type="number" style={inputStyle} value={simpleDispensaryStock} onChange={e => setSimpleDispensaryStock(parseInt(e.target.value) || 0)} onFocus={e => e.target.style.borderColor = theme.inputFocus} onBlur={e => e.target.style.borderColor = theme.inputBorder} />
                      </div>
                    )}
                  </div>

                  {/* Divider */}
                  <div style={{ height: '1px', background: theme.border, margin: '0 0 24px' }} />

                  {/* Store Stock */}
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <h3 style={{ fontSize: '15px', fontWeight: '700', color: theme.textPrimary }}>📦 Store Stock</h3>
                      <span style={{ fontSize: '12px', color: theme.textMuted, background: theme.cardBg, padding: '3px 8px', borderRadius: '20px', border: `1px solid ${theme.border}` }}>{getTotalStoreStock()} units</span>
                    </div>
                    {isBatchOrExpiryActive ? (
                      <>
                        {storeStockItems.map((item, i) => (
                          <StockItemRow key={i} item={item} index={i} theme={theme} isMobile={true} useBatch={useDrugBatchNumbers} useExpiry={useDrugExpiryDate}
                            onUpdate={(field, val) => { const u = [...storeStockItems]; u[i][field] = val; setStoreStockItems(u); }}
                            onRemove={() => { if (storeStockItems.length <= 1) { toast.warning('At least one entry required'); return; } const u = [...storeStockItems]; u.splice(i, 1); setStoreStockItems(u); }}
                          />
                        ))}
                        <AddRowButton onClick={() => setStoreStockItems([...storeStockItems, createEmptyStockItem()])} label="Add Store Row" />
                      </>
                    ) : (
                      <div style={formGroupStyle}>
                        <label style={labelStyle}>Store Quantity</label>
                        <input type="number" style={inputStyle} value={simpleStoreStock} onChange={e => setSimpleStoreStock(parseInt(e.target.value) || 0)} onFocus={e => e.target.style.borderColor = theme.inputFocus} onBlur={e => e.target.style.borderColor = theme.inputBorder} />
                      </div>
                    )}
                  </div>

                  <SaveButton onClick={handleUpdateStock} loading={isUpdatingStock} disabled={!isStockChanged()} label="Save Stock Figures" loadingLabel="Saving..." />
                </div>
              )}
            </>
          )}
        </div>

        <ToastContainer position="top-center" autoClose={4000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover theme={normalizedTheme === 'blue' ? 'dark' : 'light'} />
      </div>
    );
  }

  // ── DESKTOP MODAL ────────────────────────────────────────────────────────────
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: theme.overlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{
        backgroundColor: theme.background, borderRadius: '14px', maxWidth: '880px', width: '95%',
        maxHeight: '90vh', overflowY: 'auto', padding: '28px',
        boxShadow: normalizedTheme === 'blue' ? '0 10px 40px rgba(37,99,235,0.2)' : '0 10px 30px rgba(0,0,0,0.12)',
        position: 'relative', fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        animation: 'slideIn 0.25s ease',
      }} onClick={e => e.stopPropagation()}>

        {/* Close */}
        <button onClick={onClose} style={{ position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', color: theme.closeIcon, fontSize: '1.4rem', cursor: 'pointer' }}>
          <FontAwesomeIcon icon={faTimes} />
        </button>

        <h2 style={{ color: theme.textPrimary, marginBottom: '6px', fontSize: '22px', fontWeight: '700' }}>Update Item Settings</h2>
        <div style={{ fontSize: '12px', color: theme.textMuted, marginBottom: '16px' }}>ID: {drugDetailsState.drugId || 'N/A'} · {drugDetailsState.drugName}</div>

        {isBatchOrExpiryActive && (
          <div style={{ display: 'inline-block', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', marginBottom: '16px', background: theme.primaryBg, color: theme.primary, border: `1px solid ${theme.primaryLight}` }}>
            {useDrugBatchNumbers && useDrugExpiryDate ? '📦 Batch Numbers + Expiry Dates Active' : useDrugBatchNumbers ? '📦 Batch Numbers Active' : '📅 Expiry Dates Active'}
          </div>
        )}

        {/* Desktop Tabs */}
        <div style={{ display: 'flex', marginBottom: '24px', borderBottom: `1px solid ${theme.border}` }}>
          {[{ id: 'details', label: 'Item Details' }, { id: 'stock', label: 'Stock Management' }].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              padding: '11px 22px', border: 'none', background: activeTab === tab.id ? theme.primaryBg : 'transparent',
              cursor: 'pointer', borderBottom: activeTab === tab.id ? `3px solid ${theme.tabActiveBorder}` : '3px solid transparent',
              marginRight: '4px', fontSize: '14px', fontWeight: activeTab === tab.id ? '600' : '500',
              color: activeTab === tab.id ? theme.tabActive : theme.tabInactive, borderRadius: '6px 6px 0 0',
              transition: 'all 0.15s ease',
            }}>{tab.label}</button>
          ))}
        </div>

        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <FontAwesomeIcon icon={faSpinner} spin size="2x" style={{ color: theme.primary }} />
          </div>
        ) : (
          <>
            {/* DETAILS TAB */}
            {activeTab === 'details' && (
              <div>
                {[
                  { label: 'Item Name', name: 'drugName', type: 'text' },
                  { label: 'Warning Point (Min. Stock)', name: 'warningPoint', type: 'number' },
                  { label: 'Cost Price (UGX)', name: 'costPrice', type: 'number' },
                  { label: 'Selling Price (UGX)', name: 'sellingPrice', type: 'number' },
                ].map(field => (
                  <div key={field.name} style={formGroupStyle}>
                    <label style={labelStyle}>{field.label}</label>
                    <input type={field.type} name={field.name} style={inputStyle} value={drugDetailsState[field.name]} onChange={handleInputChange} onFocus={e => e.target.style.borderColor = theme.inputFocus} onBlur={e => e.target.style.borderColor = theme.inputBorder} />
                  </div>
                ))}
                <div style={formGroupStyle}>
                  <label style={labelStyle}>Packaging</label>
                  <select name="packaging" style={inputStyle} value={drugDetailsState.packaging} onChange={handleInputChange} onFocus={e => e.target.style.borderColor = theme.inputFocus} onBlur={e => e.target.style.borderColor = theme.inputBorder}>
                    <option value="">Select Packaging</option>
                    {availablePackaging.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <button onClick={handleUpdateDrug} disabled={!isDetailsChanged() || isUpdating} style={{
                  padding: '11px 24px', border: 'none', borderRadius: '8px',
                  background: (!isDetailsChanged() || isUpdating) ? theme.disabled : theme.primary,
                  color: '#fff', fontSize: '14px', fontWeight: '600', cursor: (!isDetailsChanged() || isUpdating) ? 'not-allowed' : 'pointer',
                }}>
                  {isUpdating ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Update Item Details'}
                </button>
              </div>
            )}

            {/* STOCK TAB */}
            {activeTab === 'stock' && (
              <div>
                {/* Totals */}
                <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
                  {[['🏪 Shelves Stock', getTotalDispensaryStock()], ['📦 Store Stock', getTotalStoreStock()]].map(([label, val]) => (
                    <div key={label} style={{ flex: 1, background: theme.primaryBg, borderRadius: '10px', padding: '14px', textAlign: 'center', border: `1px solid ${theme.border}` }}>
                      <div style={{ fontSize: '12px', color: theme.textMuted, marginBottom: '4px', fontWeight: '600' }}>{label}</div>
                      <div style={{ fontSize: '26px', fontWeight: '700', color: theme.primary }}>{val}</div>
                      <div style={{ fontSize: '11px', color: theme.textMuted }}>units</div>
                    </div>
                  ))}
                </div>

                {/* Shelves */}
                <h3 style={{ color: theme.textPrimary, marginBottom: '14px', fontSize: '16px', fontWeight: '600' }}>💊 Shelves Stock</h3>
                {isBatchOrExpiryActive ? (
                  <>
                    {dispensaryStockItems.map((item, i) => (
                      <StockItemRow key={i} item={item} index={i} theme={theme} isMobile={false} useBatch={useDrugBatchNumbers} useExpiry={useDrugExpiryDate}
                        onUpdate={(field, val) => { const u = [...dispensaryStockItems]; u[i][field] = val; setDispensaryStockItems(u); }}
                        onRemove={() => { if (dispensaryStockItems.length <= 1) { toast.warning('At least one entry required'); return; } const u = [...dispensaryStockItems]; u.splice(i, 1); setDispensaryStockItems(u); }}
                      />
                    ))}
                    <AddRowButton onClick={() => setDispensaryStockItems([...dispensaryStockItems, createEmptyStockItem()])} label="Add Shelves Stock Row" />
                  </>
                ) : (
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>Shelves Stock Quantity</label>
                    <input type="number" style={inputStyle} value={simpleDispensaryStock} onChange={e => setSimpleDispensaryStock(parseInt(e.target.value) || 0)} onFocus={e => e.target.style.borderColor = theme.inputFocus} onBlur={e => e.target.style.borderColor = theme.inputBorder} />
                  </div>
                )}

                <div style={{ height: '1px', background: theme.border, margin: '10px 0 24px' }} />

                {/* Store */}
                <h3 style={{ color: theme.textPrimary, marginBottom: '14px', fontSize: '16px', fontWeight: '600' }}>🏪 Store Stock</h3>
                {isBatchOrExpiryActive ? (
                  <>
                    {storeStockItems.map((item, i) => (
                      <StockItemRow key={i} item={item} index={i} theme={theme} isMobile={false} useBatch={useDrugBatchNumbers} useExpiry={useDrugExpiryDate}
                        onUpdate={(field, val) => { const u = [...storeStockItems]; u[i][field] = val; setStoreStockItems(u); }}
                        onRemove={() => { if (storeStockItems.length <= 1) { toast.warning('At least one entry required'); return; } const u = [...storeStockItems]; u.splice(i, 1); setStoreStockItems(u); }}
                      />
                    ))}
                    <AddRowButton onClick={() => setStoreStockItems([...storeStockItems, createEmptyStockItem()])} label="Add Store Stock Row" />
                  </>
                ) : (
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>Store Stock Quantity</label>
                    <input type="number" style={inputStyle} value={simpleStoreStock} onChange={e => setSimpleStoreStock(parseInt(e.target.value) || 0)} onFocus={e => e.target.style.borderColor = theme.inputFocus} onBlur={e => e.target.style.borderColor = theme.inputBorder} />
                  </div>
                )}

                <button onClick={handleUpdateStock} disabled={!isStockChanged() || isUpdatingStock} style={{
                  padding: '11px 24px', border: 'none', borderRadius: '8px',
                  background: (!isStockChanged() || isUpdatingStock) ? theme.disabled : theme.primary,
                  color: '#fff', fontSize: '14px', fontWeight: '600', cursor: (!isStockChanged() || isUpdatingStock) ? 'not-allowed' : 'pointer',
                }}>
                  {isUpdatingStock ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Update Stock Figures'}
                </button>
              </div>
            )}
          </>
        )}

        <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover theme={normalizedTheme === 'blue' ? 'dark' : 'light'} />
      </div>
    </div>
  );
}

export default UpdateStocksModal;